import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import GroupsIcon from "@mui/icons-material/Groups";
import BusinessIcon from "@mui/icons-material/Business";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SaveIcon from "@mui/icons-material/Save";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EpoMailTemplateManager from "./EpoMailTemplateManager";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import {
  BulkActivateEpoWorkflowRules,
  GetAllDepartments,
  getEpoWorkflowConfiguration,
  GetLimit,
  GetOfficeEmployee,
  saveEpoAuthority,
  saveEpoWorkflowRule,
  saveEpoWorkflowSteps,
} from "../../../services/admin";
import EpoWorkflowSimulator from "./EpoWorkflowSimulator";
import EpoMailTokenManager from "./EpoMailTokenManager";
import { ThemeContext } from "@emotion/react";

const get = (obj, camelKey, pascalKey, fallback = null) => {
  if (!obj) return fallback;
  if (Object.prototype.hasOwnProperty.call(obj, camelKey)) return obj[camelKey];
  if (Object.prototype.hasOwnProperty.call(obj, pascalKey))
    return obj[pascalKey];
  return fallback;
};

const list = (value) => (Array.isArray(value) ? value : []);
const normalizeText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
const normalizeId = (value) =>
  String(value ?? "")
    .trim()
    .replace(/^0+(?=\d)/, "");

const isActiveRow = (row) => {
  const value = get(row, "isActive", "IsActive", true);
  if (value === false || value === 0 || value === "0") return false;
  if (typeof value === "string") {
    return !["N", "NO", "FALSE", "INACTIVE"].includes(value.toUpperCase());
  }
  return true;
};

const codeOf = (row, camelKey, pascalKey) =>
  normalizeText(get(row, camelKey, pascalKey, "")).toUpperCase();

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(number);
};

const getErrorMessage = (error, fallback = "Operation failed.") => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || data?.Message || error?.message || fallback;
};

const employeeDisplayName = (row) => {
  const firstName = normalizeText(get(row, "firstName", "FirstName", ""));
  const lastName = normalizeText(get(row, "lastName", "LastName", ""));
  const personName = normalizeText(
    get(
      row,
      "personName",
      "PersonName",
      get(
        row,
        "fullName",
        "FullName",
        get(row, "employeeName", "EmployeeName", ""),
      ),
    ),
  );
  if (firstName || lastName) return normalizeText(`${firstName} ${lastName}`);
  const parts = personName.split(" ").filter(Boolean);
  if (parts.length <= 1) return parts[0] || "";
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

const normalizeEmployee = (row) => {
  const personId = get(
    row,
    "employeeId",
    "EmployeeId",
    get(row, "personId", "PersonId", get(row, "personID", "PersonID", null)),
  );

  return {
    raw: row,
    userId: get(row, "userId", "UserId", null),
    personId,
    employeeCode: normalizeText(
      get(
        row,
        "employeeId",
        "EmployeeId",
        get(row, "employeeCode", "EmployeeCode", personId),
      ),
    ),
    displayName: employeeDisplayName(row),
    fullName: normalizeText(
      get(
        row,
        "personName",
        "PersonName",
        get(row, "fullName", "FullName", employeeDisplayName(row)),
      ),
    ),
    email: normalizeText(
      get(row, "emailAddress", "EmailAddress", get(row, "email", "Email", "")),
    ),
    departmentId: get(
      row,
      "departmentId",
      "DepartmentId",
      get(row, "empDeptID", "EmpDeptID", null),
    ),
    departmentName: normalizeText(
      get(
        row,
        "empDept",
        "EmpDept",
        get(row, "departmentName", "DepartmentName", ""),
      ),
    ),
    employeePosition: normalizeText(
      get(
        row,
        "employeePosition",
        "EmployeePosition",
        get(row, "empPosition", "EmpPosition", ""),
      ),
    ),
    empLimitNew: get(row, "empLimitNew", "EmpLimitNew", null),
    status: get(row, "status", "Status", null),
    isActive: get(
      row,
      "isActive",
      "IsActive",
      get(row, "activeUser", "ActiveUser", true),
    ),
  };
};

const employeeIdentityKeys = (employee) => {
  const keys = [];
  const personId = normalizeId(employee?.personId);
  const userId = normalizeId(employee?.userId);
  const employeeCode = normalizeText(employee?.employeeCode).toUpperCase();
  const email = normalizeText(employee?.email).toLowerCase();

  if (personId) keys.push(`PERSON:${personId}`);
  if (userId) keys.push(`USER:${userId}`);
  if (employeeCode) keys.push(`EMP:${employeeCode}`);
  if (email) keys.push(`EMAIL:${email}`);
  return keys;
};

const employeeKey = (employee) => {
  const keys = employeeIdentityKeys(employee);
  return (
    keys[0] || `NAME:${normalizeText(employee?.displayName).toLowerCase()}`
  );
};

const employeeIsActive = (employee) => {
  if (
    employee?.status !== null &&
    employee?.status !== undefined &&
    employee.status !== ""
  ) {
    return Number(employee.status) === 1;
  }
  const value = employee?.isActive;
  if (value === false || value === 0 || value === "0") return false;
  if (typeof value === "string") {
    return !["N", "NO", "FALSE", "INACTIVE"].includes(value.toUpperCase());
  }
  return true;
};

const mergeOfficeEmployeesWithLimits = (officeRows, limitRows) => {
  const limitByIdentity = new Map();

  list(limitRows)
    .map(normalizeEmployee)
    .forEach((employee) => {
      employeeIdentityKeys(employee).forEach((key) => {
        const current = limitByIdentity.get(key);
        const currentHasLimit =
          current?.empLimitNew !== null && current?.empLimitNew !== undefined;
        const candidateHasLimit =
          employee.empLimitNew !== null && employee.empLimitNew !== undefined;
        if (!current || (!currentHasLimit && candidateHasLimit)) {
          limitByIdentity.set(key, employee);
        }
      });
    });

  const byKey = new Map();

  list(officeRows)
    .map(normalizeEmployee)
    .filter((employee) => employee.displayName && employeeIsActive(employee))
    .forEach((officeEmployee) => {
      const limitEmployee = employeeIdentityKeys(officeEmployee)
        .map((key) => limitByIdentity.get(key))
        .find(Boolean);

      const merged = limitEmployee
        ? {
            ...officeEmployee,
            userId: officeEmployee.userId ?? limitEmployee.userId,
            personId: officeEmployee.personId ?? limitEmployee.personId,
            employeeCode:
              officeEmployee.employeeCode || limitEmployee.employeeCode,
            fullName: officeEmployee.fullName || limitEmployee.fullName,
            displayName:
              officeEmployee.displayName || limitEmployee.displayName,
            email: officeEmployee.email || limitEmployee.email,
            departmentId:
              officeEmployee.departmentId ?? limitEmployee.departmentId,
            departmentName:
              officeEmployee.departmentName || limitEmployee.departmentName,
            employeePosition:
              officeEmployee.employeePosition || limitEmployee.employeePosition,
            empLimitNew:
              officeEmployee.empLimitNew ?? limitEmployee.empLimitNew ?? null,
          }
        : officeEmployee;

      byKey.set(employeeKey(merged), merged);
    });

  return [...byKey.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, {
      sensitivity: "base",
    }),
  );
};

const filterEmployees = (options, state) => {
  const terms = normalizeText(state.inputValue)
    .toLowerCase()
    .split(" ")
    .filter(Boolean);
  if (!terms.length) return options;
  return options.filter((option) => {
    const searchable = normalizeText(
      `${option.displayName} ${option.fullName} ${option.employeeCode} ${
        option.personId ?? ""
      } ${option.departmentName}`,
    ).toLowerCase();
    return terms.every((term) => searchable.includes(term));
  });
};

const authorityRowsByKey = (authorities, routingKey) =>
  list(authorities)
    .filter(isActiveRow)
    .filter(
      (row) =>
        codeOf(row, "routingKey", "RoutingKey") ===
        String(routingKey).toUpperCase(),
    )
    .sort(
      (a, b) =>
        Number(get(a, "sequenceNo", "SequenceNo", 9999)) -
        Number(get(b, "sequenceNo", "SequenceNo", 9999)),
    );

const findRule = (rules, ruleCode) =>
  list(rules).find(
    (rule) =>
      isActiveRow(rule) &&
      codeOf(rule, "ruleCode", "RuleCode") === String(ruleCode).toUpperCase(),
  ) || null;

const stepsForRule = (steps, ruleId) =>
  list(steps)
    .filter(isActiveRow)
    .filter(
      (step) =>
        Number(get(step, "workflowRuleId", "WorkflowRuleId", 0)) ===
        Number(ruleId),
    )
    .sort(
      (a, b) =>
        Number(get(a, "stepOrder", "StepOrder", 0)) -
        Number(get(b, "stepOrder", "StepOrder", 0)),
    );

const stageTypeFromStep = (step) => {
  const sourceType = codeOf(step, "approverSourceType", "ApproverSourceType");
  const stepCode = codeOf(step, "stepCode", "StepCode");

  if (sourceType === "USER" || stepCode === "SPECIFIC_USER")
    return "SPECIFIC_USER";
  if (stepCode === "HIGHER_AUTHORITY" || stepCode === "DIRECTOR")
    return "HIGHER_AUTHORITY";
  return stepCode;
};

const stageLabel = (stageType) => {
  const labels = {
    SELF: "Self Approval",
    DEPUTY: "Department Deputy",
    MPC: "MPC Group",
    HIGHER_AUTHORITY: "Higher Authority",
    DIRECTOR: "Higher Authority",
    PRESIDENT: "President",
    IT_APPROVER: "IT Purchase Approver",
    SPECIFIC_USER: "Specific Employee",
  };
  return labels[stageType] || stageType || "Approval Step";
};

const STAGE_OPTIONS = [
  { value: "SELF", label: "Self Approval" },
  { value: "DEPUTY", label: "Department Deputy" },
  { value: "MPC", label: "MPC Group" },
  { value: "HIGHER_AUTHORITY", label: "Higher Authority" },
  { value: "PRESIDENT", label: "President" },
  { value: "IT_APPROVER", label: "IT Purchase Approver" },
  { value: "SPECIFIC_USER", label: "Specific Employee" },
];

const LIMIT_SOURCE_OPTIONS = [
  "NONE",
  "FIXED",
  "CREATOR_LIMIT",
  "DEPARTMENT_APPROVER_LIMIT",
  "AUTHORITY_LIMIT",
];

const LIMIT_KEY_OPTIONS = [
  "DEPUTY_STAGE",
  "MPC_DEFAULT",
  "IT_SOFTWARE_DEPUTY",
  "HIGHER_AUTHORITY",
  "PRESIDENT",
  "IT_APPROVER_PRIMARY",
];

const rulePayload = (rule, overrides = {}) => ({
  action: "UPSERT",
  id: get(rule, "id", "Id", null),
  ruleCode: get(rule, "ruleCode", "RuleCode", ""),
  ruleName: get(rule, "ruleName", "RuleName", ""),
  priority: Number(get(rule, "priority", "Priority", 0)),
  isItPurchase: get(rule, "isItPurchase", "IsItPurchase", null),
  creatorAuthorityType: get(
    rule,
    "creatorAuthorityType",
    "CreatorAuthorityType",
    null,
  ),
  creatorUserId: get(rule, "creatorUserId", "CreatorUserId", null),
  departmentId: get(rule, "departmentId", "DepartmentId", null),
  minLimitSource: get(rule, "minLimitSource", "MinLimitSource", "NONE"),
  minLimitKey: get(rule, "minLimitKey", "MinLimitKey", null),
  minAmount: get(rule, "minAmount", "MinAmount", null),
  minInclusive: Boolean(get(rule, "minInclusive", "MinInclusive", false)),
  maxLimitSource: get(rule, "maxLimitSource", "MaxLimitSource", "NONE"),
  maxLimitKey: get(rule, "maxLimitKey", "MaxLimitKey", null),
  maxAmount: get(rule, "maxAmount", "MaxAmount", null),
  maxInclusive: Boolean(get(rule, "maxInclusive", "MaxInclusive", true)),
  isActive: Boolean(get(rule, "isActive", "IsActive", true)),
  ...overrides,
});

const describeLimit = (source, key, amount) => {
  const normalized = normalizeText(source).toUpperCase();
  if (normalized === "NONE") return "No limit";
  if (normalized === "FIXED") return formatCurrency(amount);
  if (normalized === "CREATOR_LIMIT") return "Creator limit";
  if (normalized === "DEPARTMENT_APPROVER_LIMIT")
    return "Department Deputy limit";
  if (normalized === "AUTHORITY_LIMIT") {
    const labels = {
      HIGHER_AUTHORITY: "Higher Authority limit",
      PRESIDENT: "President limit",
      IT_APPROVER_PRIMARY: "IT Approver limit",
      MPC_DEFAULT: "MPC authority limit",
      DEPUTY_STAGE: "Department Deputy limit",
    };
    return (
      labels[String(key || "").toUpperCase()] || `${key || "Authority"} limit`
    );
  }
  return normalized;
};

const ruleRangeText = (rule) => {
  const minSource = get(rule, "minLimitSource", "MinLimitSource", "NONE");
  const maxSource = get(rule, "maxLimitSource", "MaxLimitSource", "NONE");
  const minKey = get(rule, "minLimitKey", "MinLimitKey", null);
  const maxKey = get(rule, "maxLimitKey", "MaxLimitKey", null);
  const minAmount = get(rule, "minAmount", "MinAmount", null);
  const maxAmount = get(rule, "maxAmount", "MaxAmount", null);
  const minInclusive = Boolean(
    get(rule, "minInclusive", "MinInclusive", false),
  );
  const maxInclusive = Boolean(get(rule, "maxInclusive", "MaxInclusive", true));

  const minLabel = describeLimit(minSource, minKey, minAmount);
  const maxLabel = describeLimit(maxSource, maxKey, maxAmount);

  if (String(minSource).toUpperCase() === "NONE") {
    return `Amount ${maxInclusive ? "≤" : "<"} ${maxLabel}`;
  }
  if (String(maxSource).toUpperCase() === "NONE") {
    return `Amount ${minInclusive ? "≥" : ">"} ${minLabel}`;
  }
  return `${minLabel} ${minInclusive ? "≤" : "<"} Amount ${
    maxInclusive ? "≤" : "<"
  } ${maxLabel}`;
};

const SummaryCard = ({ icon, label, value, caption }) => (
  <Paper
    variant="outlined"
    sx={{
      height: "100%",
      px: 1.25,
      py: 0.9,
      borderRadius: 1.5,
      bgcolor: "background.paper",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Avatar
        sx={{
          width: 30,
          height: 30,
          bgcolor: "action.hover",
          color: "primary.main",
          "& svg": { fontSize: 17 },
        }}
      >
        {icon}
      </Avatar>
      <Box minWidth={0}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          fontWeight={700}
          noWrap
          sx={{ lineHeight: 1.15, mt: 0.15 }}
        >
          {value}
        </Typography>
        {caption ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap
            sx={{ mt: 0.15, fontSize: "0.68rem" }}
          >
            {caption}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  </Paper>
);

const UserSummary = ({ row }) => {
  const name = get(row, "fullName", "FullName", "Not configured");
  const email = get(row, "emailAddress", "EmailAddress", "");
  const position = get(row, "employeePosition", "EmployeePosition", "");

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Avatar sx={{ width: 34, height: 34, fontSize: 14 }}>
        {String(name || "?")
          .charAt(0)
          .toUpperCase()}
      </Avatar>
      <Box minWidth={0}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          noWrap
        >
          {email || "No email"}
          {position ? ` • ${position}` : ""}
        </Typography>
      </Box>
    </Stack>
  );
};

const RuleDialog = ({ open, rule, nextPriority, saving, onClose, onSave }) => {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      setForm(rulePayload(rule));
    } else {
      setForm({
        action: "UPSERT",
        id: null,
        ruleCode: "",
        ruleName: "",
        priority: nextPriority,
        isItPurchase: false,
        creatorAuthorityType: null,
        creatorUserId: null,
        departmentId: null,
        minLimitSource: "NONE",
        minLimitKey: null,
        minAmount: null,
        minInclusive: false,
        maxLimitSource: "NONE",
        maxLimitKey: null,
        maxAmount: null,
        maxInclusive: true,
        isActive: true,
      });
    }
  }, [open, rule, nextPriority]);

  if (!form) return null;

  const change = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const needsMinKey = ["AUTHORITY_LIMIT", "DEPARTMENT_APPROVER_LIMIT"].includes(
    form.minLimitSource,
  );
  const needsMaxKey = ["AUTHORITY_LIMIT", "DEPARTMENT_APPROVER_LIMIT"].includes(
    form.maxLimitSource,
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {rule ? "Edit Workflow Rule" : "Add Workflow Rule"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Rule Code"
              value={form.ruleCode}
              disabled={Boolean(rule)}
              onChange={(event) =>
                change(
                  "ruleCode",
                  event.target.value.toUpperCase().replace(/\s+/g, "_"),
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Rule Name"
              value={form.ruleName}
              onChange={(event) => change("ruleName", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={form.priority}
              onChange={(event) =>
                change("priority", Number(event.target.value))
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Purchase Type"
              value={
                form.isItPurchase === null
                  ? "ALL"
                  : form.isItPurchase
                  ? "IT"
                  : "NORMAL"
              }
              onChange={(event) =>
                change(
                  "isItPurchase",
                  event.target.value === "ALL"
                    ? null
                    : event.target.value === "IT",
                )
              }
            >
              <MenuItem value="ALL">All EPO</MenuItem>
              <MenuItem value="NORMAL">Normal EPO</MenuItem>
              <MenuItem value="IT">IT Purchase</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.isActive)}
                  onChange={(event) => change("isActive", event.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Minimum Source"
              value={form.minLimitSource}
              onChange={(event) => change("minLimitSource", event.target.value)}
            >
              {LIMIT_SOURCE_OPTIONS.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            {form.minLimitSource === "FIXED" ? (
              <TextField
                fullWidth
                type="number"
                label="Minimum Amount"
                value={form.minAmount ?? ""}
                onChange={(event) =>
                  change(
                    "minAmount",
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                  )
                }
              />
            ) : (
              <TextField
                select={needsMinKey}
                fullWidth
                disabled={!needsMinKey}
                label="Minimum Limit Key"
                value={form.minLimitKey || ""}
                onChange={(event) =>
                  change("minLimitKey", event.target.value || null)
                }
              >
                {needsMinKey
                  ? LIMIT_KEY_OPTIONS.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))
                  : null}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.minInclusive)}
                  onChange={(event) =>
                    change("minInclusive", event.target.checked)
                  }
                />
              }
              label="Minimum inclusive"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Maximum Source"
              value={form.maxLimitSource}
              onChange={(event) => change("maxLimitSource", event.target.value)}
            >
              {LIMIT_SOURCE_OPTIONS.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            {form.maxLimitSource === "FIXED" ? (
              <TextField
                fullWidth
                type="number"
                label="Maximum Amount"
                value={form.maxAmount ?? ""}
                onChange={(event) =>
                  change(
                    "maxAmount",
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                  )
                }
              />
            ) : (
              <TextField
                select={needsMaxKey}
                fullWidth
                disabled={!needsMaxKey}
                label="Maximum Limit Key"
                value={form.maxLimitKey || ""}
                onChange={(event) =>
                  change("maxLimitKey", event.target.value || null)
                }
              >
                {needsMaxKey
                  ? LIMIT_KEY_OPTIONS.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))
                  : null}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.maxInclusive)}
                  onChange={(event) =>
                    change("maxInclusive", event.target.checked)
                  }
                />
              }
              label="Maximum inclusive"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          disabled={
            saving ||
            !form.ruleCode.trim() ||
            !form.ruleName.trim() ||
            Number(form.priority) <= 0
          }
          onClick={() => onSave(form)}
        >
          Save Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SequenceEditor = ({ rule, steps, employees, saving, onSave }) => {
  const [draft, setDraft] = useState([]);
  const ruleId = get(rule, "id", "Id", null);

  useEffect(() => {
    setDraft(
      stepsForRule(steps, ruleId).map((step) => ({
        id: get(step, "id", "Id", null),
        stageType: stageTypeFromStep(step),
        approverUserId: get(step, "approverUserId", "ApproverUserId", null),
      })),
    );
  }, [steps, ruleId]);

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= draft.length) return;
    setDraft((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const update = (index, patch) =>
    setDraft((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );

  const add = () =>
    setDraft((current) => [
      ...current,
      { stageType: "SPECIFIC_USER", approverUserId: null },
    ]);
  const remove = (index) =>
    setDraft((current) => current.filter((_, rowIndex) => rowIndex !== index));

  return (
    <Stack spacing={1.5}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={55}>#</TableCell>
              <TableCell>Approval Stage</TableCell>
              <TableCell>Specific Employee</TableCell>
              <TableCell align="right">Order / Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {draft.map((row, index) => {
              const selectedEmployee =
                employees.find(
                  (employee) =>
                    Number(employee.userId) === Number(row.approverUserId),
                ) || null;

              return (
                <TableRow key={`${row.id || "NEW"}-${index}`}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.stageType}
                      onChange={(event) =>
                        update(index, {
                          stageType: event.target.value,
                          approverUserId:
                            event.target.value === "SPECIFIC_USER"
                              ? row.approverUserId
                              : null,
                        })
                      }
                      sx={{ minWidth: 220 }}
                    >
                      {STAGE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {row.stageType === "SPECIFIC_USER" ? (
                      <Autocomplete
                        size="small"
                        options={employees}
                        value={selectedEmployee}
                        onChange={(_, value) =>
                          update(index, {
                            approverUserId: value?.userId ?? null,
                          })
                        }
                        filterOptions={filterEmployees}
                        getOptionLabel={(option) => option?.displayName || ""}
                        isOptionEqualToValue={(option, value) =>
                          Number(option.userId) === Number(value.userId)
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Approver" />
                        )}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Resolved from {stageLabel(row.stageType)} configuration
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={0.25}
                    >
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === draft.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={draft.length <= 1}
                        onClick={() => remove(index)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
      >
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={add}
          disabled={saving}
        >
          Add Step
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          disabled={
            saving ||
            !draft.length ||
            draft.some(
              (row) => row.stageType === "SPECIFIC_USER" && !row.approverUserId,
            )
          }
          onClick={() => onSave(ruleId, draft)}
        >
          Save Sequence to DB
        </Button>
      </Stack>
    </Stack>
  );
};

const FlowCard = ({
  rule,
  allSteps,
  employees,
  saving,
  onSaveSteps,
  onEditRule,
  onDeleteRule,
  inactive = false,
}) => {
  const [open, setOpen] = useState(false);
  const ruleId = get(rule, "id", "Id", null);
  const steps = stepsForRule(allSteps, ruleId);

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1.15 }}>
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                color="primary"
                label={`Priority ${get(rule, "priority", "Priority", "—")}`}
              />
              <Typography variant="subtitle1" fontWeight={700}>
                {get(rule, "ruleName", "RuleName", "Workflow Rule")}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={get(rule, "ruleCode", "RuleCode", "")}
              />
              {get(rule, "isItPurchase", "IsItPurchase", null) === true ? (
                <Chip size="small" variant="outlined" label="IT Purchase" />
              ) : null}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25 }}
            >
              {ruleRangeText(rule)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Edit rule">
              <IconButton size="small" onClick={() => onEditRule(rule)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete rule">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDeleteRule(rule)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant={open ? "contained" : "outlined"}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? "Close Sequence" : "Configure Sequence"}
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          {steps.map((step, index) => {
            const stageType = stageTypeFromStep(step);
            const specificUserId = get(
              step,
              "approverUserId",
              "ApproverUserId",
              null,
            );
            const specificEmployee = employees.find(
              (employee) => Number(employee.userId) === Number(specificUserId),
            );
            const label =
              stageType === "SPECIFIC_USER"
                ? specificEmployee?.displayName ||
                  `Specific User ${specificUserId || ""}`
                : stageLabel(stageType);

            return (
              <React.Fragment key={get(step, "id", "Id", index)}>
                {index > 0 ? (
                  <Typography color="text.secondary">→</Typography>
                ) : null}
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={label}
                />
              </React.Fragment>
            );
          })}
          {!steps.length ? (
            <Alert severity="warning">No approval steps are configured.</Alert>
          ) : null}
        </Stack>

        {open ? (
          <>
            <Divider />
            <SequenceEditor
              rule={rule}
              steps={allSteps}
              employees={employees}
              saving={saving}
              onSave={onSaveSteps}
            />
          </>
        ) : null}
      </Stack>
    </Paper>
  );
};

const ThresholdEditor = ({ rules, authorities, saving, onSave }) => {
  const mpcRule = findRule(rules, "MPC");
  const higherAuthority =
    authorityRowsByKey(authorities, "HIGHER_AUTHORITY")[0] || null;
  const itApprover =
    authorityRowsByKey(authorities, "IT_APPROVER_PRIMARY")[0] || null;

  const currentMpc = get(mpcRule, "maxAmount", "MaxAmount", "");
  const currentHigher = get(
    higherAuthority,
    "approvalLimit",
    "ApprovalLimit",
    "",
  );
  const currentIt = get(itApprover, "approvalLimit", "ApprovalLimit", "");

  const [mpcLimit, setMpcLimit] = useState(currentMpc ?? "");
  const [higherLimit, setHigherLimit] = useState(currentHigher ?? "");
  const [itLimit, setItLimit] = useState(currentIt ?? "");

  useEffect(() => setMpcLimit(currentMpc ?? ""), [currentMpc]);
  useEffect(() => setHigherLimit(currentHigher ?? ""), [currentHigher]);
  useEffect(() => setItLimit(currentIt ?? ""), [currentIt]);

  const valid =
    Number(mpcLimit) > 0 &&
    Number(higherLimit) > Number(mpcLimit) &&
    Number(itLimit) > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Approval Amount Thresholds
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem" }}
          >
            These values are saved immediately to the new workflow master
            configuration.
          </Typography>
        </Box>

        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={3.5}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="MPC Upper Limit"
              value={mpcLimit}
              onChange={(e) => setMpcLimit(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3.5}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Higher Authority Limit"
              value={higherLimit}
              onChange={(e) => setHigherLimit(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3.5}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="IT Approver Limit"
              value={itLimit}
              onChange={(e) => setItLimit(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={15} /> : <SaveIcon />}
              disabled={saving || !valid}
              onClick={() =>
                onSave({
                  mpcLimit: Number(mpcLimit),
                  higherLimit: Number(higherLimit),
                  itLimit: Number(itLimit),
                })
              }
              sx={{
                minHeight: 40,
                whiteSpace: "nowrap",
              }}
            >
              Save
            </Button>
          </Grid>
        </Grid>

        {!valid ? (
          <Typography
            variant="caption"
            color="error"
            sx={{ fontSize: "0.72rem" }}
          >
            MPC must be greater than 0, Higher Authority must be above MPC, and
            IT limit must be greater than 0.
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
};

const DepartmentRouting = ({ authorities, employees, saving, onSave }) => {
  const allRows = list(authorities)
    .filter((row) => codeOf(row, "routingKey", "RoutingKey") === "DEPUTY_STAGE")
    .sort((a, b) =>
      String(get(a, "departmentName", "DepartmentName", "")).localeCompare(
        String(get(b, "departmentName", "DepartmentName", "")),
      ),
    );

  const rows = allRows.filter(isActiveRow);
  const inactiveRows = allRows.filter((row) => !isActiveRow(row));
  const [editingId, setEditingId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [limitValue, setLimitValue] = useState("");
  const [newDepartment, setNewDepartment] = useState(null);
  const [newApprover, setNewApprover] = useState(null);
  const [newLimit, setNewLimit] = useState("");
  const [departments, setDepartments] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [inactiveExpanded, setInactiveExpanded] = useState(false);
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    action: null,
    row: null,
  });

  useEffect(() => {
    let mounted = true;

    const loadDepartments = async () => {
      try {
        setDepartmentLoading(true);

        const response = await GetAllDepartments();

        const data = Array.isArray(response)
          ? response
          : response?.data || response?.result || [];

        if (mounted) {
          setDepartments(
            data
              .map((item) => ({
                id: get(
                  item,
                  "id",
                  "Id",
                  get(item, "departmentId", "DepartmentId", null),
                ),
                name: normalizeText(
                  get(
                    item,
                    "departmentName",
                    "DepartmentName",
                    get(item, "name", "Name", ""),
                  ),
                ),
              }))
              .filter((item) => item.id !== null && item.name)
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  sensitivity: "base",
                }),
              ),
          );
        }
      } catch (error) {
        console.error("Failed to load departments", error);
      } finally {
        if (mounted) {
          setDepartmentLoading(false);
        }
      }
    };

    loadDepartments();

    return () => {
      mounted = false;
    };
  }, []);

  const departmentOptions = useMemo(() => {
    const map = new Map();
    employees.forEach((employee) => {
      if (employee.departmentId === null || employee.departmentId === undefined)
        return;
      if (!employee.departmentName) return;
      map.set(String(employee.departmentId), {
        id: employee.departmentId,
        name: employee.departmentName,
      });
    });
    rows.forEach((row) => {
      const id = get(row, "departmentId", "DepartmentId", null);
      const name = get(row, "departmentName", "DepartmentName", "");
      if (id !== null && id !== undefined && name) {
        map.set(String(id), { id, name });
      }
    });
    return [...map.values()].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }, [employees, rows]);

  const mappedDepartmentIds = useMemo(
    () =>
      new Set(
        allRows.map((row) =>
          String(get(row, "departmentId", "DepartmentId", "")),
        ),
      ),
    [allRows],
  );

  const unmappedDepartments = departments.filter(
    (department) => !mappedDepartmentIds.has(String(department.id)),
  );

  const edit = (row) => {
    setEditingId(get(row, "id", "Id", null));
    setSelectedEmployee(
      employees.find(
        (employee) =>
          Number(employee.userId) ===
          Number(get(row, "userId", "UserId", null)),
      ) || null,
    );
    setLimitValue(get(row, "approvalLimit", "ApprovalLimit", "") ?? "");
  };

  const cancel = () => {
    setEditingId(null);
    setSelectedEmployee(null);
    setLimitValue("");
  };

  const openStatusDialog = (action, row) => {
    setStatusDialog({
      open: true,
      action,
      row,
    });
  };

  const closeStatusDialog = () => {
    if (saving) return;

    setStatusDialog({
      open: false,
      action: null,
      row: null,
    });
  };

  const confirmStatusChange = async () => {
    const { action, row } = statusDialog;

    if (!row || !action) return;

    await onSave({
      action: "UPSERT",
      id: get(row, "id", "Id", null),
      routingKey: "DEPUTY_STAGE",
      departmentId: get(row, "departmentId", "DepartmentId", null),
      selectedUserId: get(row, "userId", "UserId", null),
      approvalLimit: Number(get(row, "approvalLimit", "ApprovalLimit", 0)),
      sequenceNo: Number(get(row, "sequenceNo", "SequenceNo", 1)),
      isActive: action === "activate",
    });

    closeStatusDialog();
  };

  const resetNew = () => {
    setNewDepartment(null);
    setNewApprover(null);
    setNewLimit("");
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Department Deputy Routing</Typography>
          <Typography variant="body2" color="text.secondary">
            Change the actual Deputy-stage approver and limit stored in
            master.EpoAuthorityMaster.
          </Typography>
        </Box>

        {unmappedDepartments.length ? (
          <Paper variant="outlined" sx={{ p: 1.25 }}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={700}>
                Add Department Mapping
              </Typography>
              <Grid container spacing={1.25} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    size="small"
                    options={unmappedDepartments}
                    value={newDepartment}
                    loading={departmentLoading}
                    onChange={(_, value) => setNewDepartment(value)}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Department"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {departmentLoading ? (
                                <CircularProgress size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    options={employees}
                    value={newApprover}
                    onChange={(_, value) => {
                      setNewApprover(value);
                      if (
                        value?.empLimitNew !== null &&
                        value?.empLimitNew !== undefined
                      ) {
                        setNewLimit(value.empLimitNew);
                      }
                    }}
                    filterOptions={filterEmployees}
                    getOptionLabel={(option) => option?.displayName || ""}
                    isOptionEqualToValue={(option, value) =>
                      Number(option.userId) === Number(value.userId)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Deputy / Approver" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="Approval Limit"
                    value={newLimit}
                    onChange={(e) => setNewLimit(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    size="small"
                    fullWidth
                    variant="contained"
                    disabled={
                      saving ||
                      !newDepartment ||
                      !newApprover ||
                      newLimit === ""
                    }
                    onClick={async () => {
                      await onSave({
                        action: "UPSERT",
                        id: null,
                        routingKey: "DEPUTY_STAGE",
                        departmentId: newDepartment.id,
                        selectedUserId: newApprover.userId,
                        approvalLimit: Number(newLimit),
                        sequenceNo: 1,
                        isActive: true,
                      });
                      resetNew();
                    }}
                  >
                    Add Mapping
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        ) : null}

        <TableContainer
          sx={{
            maxHeight: 410,
            overflowY: "auto",
            overflowX: "auto",

            "&::-webkit-scrollbar": {
              width: 7,
              height: 7,
            },

            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },

            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: 4,
            },

            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell>Configured Approver</TableCell>
                <TableCell align="right">Approval Limit</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const id = get(row, "id", "Id", null);
                const editing = Number(editingId) === Number(id);

                return (
                  <TableRow key={id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>
                        {get(row, "departmentName", "DepartmentName", "—")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 300 }}>
                      {editing ? (
                        <Autocomplete
                          size="small"
                          options={employees}
                          value={selectedEmployee}
                          onChange={(_, value) => {
                            setSelectedEmployee(value);
                            if (
                              value?.empLimitNew !== null &&
                              value?.empLimitNew !== undefined
                            ) {
                              setLimitValue(value.empLimitNew);
                            }
                          }}
                          filterOptions={filterEmployees}
                          getOptionLabel={(option) => option?.displayName || ""}
                          isOptionEqualToValue={(option, value) =>
                            Number(option.userId) === Number(value.userId)
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Deputy / Approver" />
                          )}
                        />
                      ) : (
                        <UserSummary row={row} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 170 }}>
                      {editing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={limitValue}
                          onChange={(e) => setLimitValue(e.target.value)}
                          inputProps={{ style: { textAlign: "right" } }}
                        />
                      ) : (
                        formatCurrency(
                          get(row, "approvalLimit", "ApprovalLimit", null),
                        )
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {editing ? (
                        <Stack
                          direction="row"
                          justifyContent="flex-end"
                          spacing={1}
                        >
                          <Button
                            size="small"
                            onClick={cancel}
                            disabled={saving}
                          >
                            Cancel
                          </Button>

                          <Button
                            size="small"
                            variant="contained"
                            disabled={
                              saving ||
                              !selectedEmployee ||
                              Number(limitValue) < 0
                            }
                            onClick={async () => {
                              await onSave({
                                action: "UPSERT",
                                id,
                                routingKey: "DEPUTY_STAGE",
                                departmentId: get(
                                  row,
                                  "departmentId",
                                  "DepartmentId",
                                  null,
                                ),
                                selectedUserId: selectedEmployee.userId,
                                approvalLimit: Number(limitValue),
                                sequenceNo: 1,
                                isActive: true,
                              });

                              cancel();
                            }}
                          >
                            Save
                          </Button>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          justifyContent="flex-end"
                          spacing={0.5}
                        >
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => edit(row)}
                            disabled={saving}
                          >
                            Edit
                          </Button>

                          <Button
                            size="small"
                            color="error"
                            onClick={() => openStatusDialog("deactivate", row)}
                            disabled={saving}
                          >
                            Inactive
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {inactiveRows.length > 0 ? (
          <Accordion
            expanded={inactiveExpanded}
            onChange={(_, expanded) => setInactiveExpanded(expanded)}
            variant="outlined"
            disableGutters
            sx={{
              mt: 2,
              boxShadow: "none",
              backgroundColor: "#F8F8F8",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                "&.Mui-expanded": {
                  minHeight: 48,
                },
                "& .MuiAccordionSummary-content": {
                  my: 1,
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  my: 1,
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700} color={"primary.main"}>
                  Inactive Department Routing
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                These department mappings are inactive and are not used in the
                approval flow.
              </Typography>

              <TableContainer
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Department</TableCell>
                      <TableCell>Configured Approver</TableCell>
                      <TableCell align="right">Approval Limit</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {inactiveRows.map((row) => {
                      const id = get(row, "id", "Id", null);

                      return (
                        <TableRow key={id} hover>
                          <TableCell>
                            <Typography fontWeight={700}>
                              {get(
                                row,
                                "departmentName",
                                "DepartmentName",
                                "—",
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ minWidth: 300 }}>
                            <UserSummary row={row} />
                          </TableCell>

                          <TableCell align="right">
                            {formatCurrency(
                              get(row, "approvalLimit", "ApprovalLimit", null),
                            )}
                          </TableCell>

                          <TableCell align="right">
                            <Button
                              size="small"
                              color="success"
                              disabled={saving}
                              onClick={() => openStatusDialog("activate", row)}
                            >
                              Activate
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Stack>
      <Dialog
        open={statusDialog.open}
        onClose={saving ? undefined : closeStatusDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {statusDialog.action === "activate"
            ? "Activate Department Routing"
            : "Deactivate Department Routing"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body1">
              Are you sure you want to{" "}
              <strong>
                {statusDialog.action === "activate" ? "activate" : "deactivate"}
              </strong>{" "}
              this department routing?
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor:
                  statusDialog.action === "activate"
                    ? "success.50"
                    : "error.50",
              }}
            >
              <Stack spacing={0.75}>
                <Typography variant="body2">
                  <strong>Department:</strong>{" "}
                  {get(
                    statusDialog.row,
                    "departmentName",
                    "DepartmentName",
                    "—",
                  )}
                </Typography>

                <Typography variant="body2">
                  <strong>Approver:</strong>{" "}
                  {get(statusDialog.row, "fullName", "FullName", "—")}
                </Typography>

                <Typography variant="body2">
                  <strong>Approval Limit:</strong>{" "}
                  {formatCurrency(
                    get(
                      statusDialog.row,
                      "approvalLimit",
                      "ApprovalLimit",
                      null,
                    ),
                  )}
                </Typography>
              </Stack>
            </Paper>

            <Alert
              severity={
                statusDialog.action === "activate" ? "success" : "warning"
              }
            >
              {statusDialog.action === "activate"
                ? "This department will become available in the active EPO routing."
                : "This department will no longer be used in the active EPO routing."}
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeStatusDialog} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color={statusDialog.action === "activate" ? "success" : "error"}
            onClick={confirmStatusChange}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={18} />
            ) : statusDialog.action === "activate" ? (
              "Activate"
            ) : (
              "Inactive"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const MpcEditor = ({ authorities, employees, saving, onSave }) => {
  const allRows = list(authorities)
    .filter((row) => codeOf(row, "routingKey", "RoutingKey") === "MPC_DEFAULT")
    .sort(
      (a, b) =>
        Number(get(a, "sequenceNo", "SequenceNo", 9999)) -
        Number(get(b, "sequenceNo", "SequenceNo", 9999)),
    );

  const rows = allRows.filter(isActiveRow);
  const inactiveRows = allRows.filter((row) => !isActiveRow(row));
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [approvalLimit, setApprovalLimit] = useState("");
  const [sequenceNo, setSequenceNo] = useState(rows.length + 1);
  const [editingId, setEditingId] = useState(null);
  const [inactiveExpanded, setInactiveExpanded] = useState(false);
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    action: null,
    row: null,
  });
  const openStatusDialog = (action, row) => {
    setStatusDialog({
      open: true,
      action,
      row,
    });
  };

  const closeStatusDialog = () => {
    if (saving) return;

    setStatusDialog({
      open: false,
      action: null,
      row: null,
    });
  };

  const confirmStatusChange = async () => {
    const { action, row } = statusDialog;

    if (!row || !action) return;

    await onSave({
      action: "UPSERT",
      id: get(row, "id", "Id", null),
      routingKey: "MPC_DEFAULT",
      selectedUserId: get(row, "userId", "UserId", null),
      approvalLimit: Number(get(row, "approvalLimit", "ApprovalLimit", 0)),
      sequenceNo: Number(get(row, "sequenceNo", "SequenceNo", 1)),
      isActive: action === "activate",
    });

    closeStatusDialog();
  };

  useEffect(() => {
    if (!editingId) {
      const used = new Set(
        rows.map((row) => Number(get(row, "sequenceNo", "SequenceNo", 0))),
      );
      let next = 1;
      while (used.has(next)) next += 1;
      setSequenceNo(next);
    }
  }, [rows, editingId]);

  const reset = () => {
    setEditingId(null);
    setSelectedEmployee(null);
    setApprovalLimit("");
  };

  const edit = (row) => {
    setEditingId(get(row, "id", "Id", null));

    setSequenceNo(Number(get(row, "sequenceNo", "SequenceNo", 1)));

    setSelectedEmployee(
      employees.find(
        (employee) =>
          Number(employee.userId) ===
          Number(get(row, "userId", "UserId", null)),
      ) || null,
    );

    setApprovalLimit(get(row, "approvalLimit", "ApprovalLimit", "") ?? "");
  };

  const duplicate = selectedEmployee
    ? rows.some(
        (row) =>
          Number(get(row, "userId", "UserId", null)) ===
            Number(selectedEmployee.userId) &&
          Number(get(row, "id", "Id", null)) !== Number(editingId),
      )
    : false;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6">MPC Members</Typography>
          <Typography variant="body2" color="text.secondary">
            The MPC list is dynamic. Every active member in this list is part of
            the MPC approval group.
          </Typography>
        </Box>

        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={employees}
              value={selectedEmployee}
              onChange={(_, value) => {
                setSelectedEmployee(value);

                // Only provide a default when adding a new MPC member.
                if (!editingId) {
                  setApprovalLimit(
                    value?.empLimitNew !== null &&
                      value?.empLimitNew !== undefined
                      ? value.empLimitNew
                      : "",
                  );
                }
              }}
              filterOptions={filterEmployees}
              getOptionLabel={(option) => option?.displayName || ""}
              isOptionEqualToValue={(option, value) =>
                Number(option.userId) === Number(value.userId)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={editingId ? "MPC Member" : "Add MPC Member"}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="MPC Limit"
              value={approvalLimit}
              onChange={(e) => setApprovalLimit(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Sequence"
              value={sequenceNo}
              onChange={(e) => setSequenceNo(Number(e.target.value))}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Button
                fullWidth
                size="small"
                variant="contained"
                disabled={
                  saving ||
                  !selectedEmployee ||
                  duplicate ||
                  approvalLimit === "" ||
                  Number(approvalLimit) < 0 ||
                  Number(sequenceNo) <= 0
                }
                onClick={async () => {
                  await onSave({
                    action: "UPSERT",
                    id: editingId,
                    routingKey: "MPC_DEFAULT",
                    selectedUserId: selectedEmployee.userId,
                    approvalLimit: Number(approvalLimit),
                    sequenceNo: Number(sequenceNo),
                    isActive: true,
                  });

                  reset();
                }}
                sx={{
                  minHeight: 40,
                }}
              >
                {editingId ? "Update" : "Add"}
              </Button>

              {editingId ? (
                <Button size="small" onClick={reset} sx={{ minHeight: 40 }}>
                  Cancel
                </Button>
              ) : null}
            </Stack>
          </Grid>
        </Grid>

        {duplicate ? (
          <Alert severity="warning">
            This employee is already an active MPC member.
          </Alert>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={90}>Sequence</TableCell>
                <TableCell>Member</TableCell>
                <TableCell align="right">Employee Limit</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={get(row, "id", "Id", null)}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={`MPC ${get(row, "sequenceNo", "SequenceNo", "—")}`}
                    />
                  </TableCell>
                  <TableCell>
                    <UserSummary row={row} />
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      get(row, "approvalLimit", "ApprovalLimit", null),
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={0.5}
                    >
                      <Button size="small" onClick={() => edit(row)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={saving}
                        onClick={() => openStatusDialog("deactivate", row)}
                      >
                        Inactive
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {inactiveRows.length > 0 ? (
          <Accordion
            expanded={inactiveExpanded}
            onChange={(_, expanded) => setInactiveExpanded(expanded)}
            variant="outlined"
            disableGutters
            sx={{
              mt: 2,
              backgroundColor: "#F8F8F8",
              boxShadow: "none",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                "&.Mui-expanded": {
                  minHeight: 48,
                },
                "& .MuiAccordionSummary-content": {
                  my: 1,
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  my: 1,
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700} color={"primary.main"}>
                  Inactive MPC Members
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                These members are inactive and are not included in the current
                MPC approval group.
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={100}>Sequence</TableCell>

                      <TableCell>Member</TableCell>

                      <TableCell align="right">MPC Limit</TableCell>

                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {inactiveRows.map((row) => (
                      <TableRow key={get(row, "id", "Id", null)} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            label={`MPC ${get(
                              row,
                              "sequenceNo",
                              "SequenceNo",
                              "—",
                            )}`}
                          />
                        </TableCell>

                        <TableCell>
                          <UserSummary row={row} />
                        </TableCell>

                        <TableCell align="right">
                          {formatCurrency(
                            get(row, "approvalLimit", "ApprovalLimit", null),
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Button
                            size="small"
                            color="success"
                            disabled={saving}
                            onClick={() => openStatusDialog("activate", row)}
                          >
                            Activate
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Stack>
      <Dialog
        open={statusDialog.open}
        onClose={saving ? undefined : closeStatusDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {statusDialog.action === "activate"
            ? "Activate MPC Member"
            : "Deactivate MPC Member"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body1">
              Are you sure you want to{" "}
              <strong>
                {statusDialog.action === "activate"
                  ? "activate"
                  : "make inactive"}
              </strong>{" "}
              this MPC member?
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor:
                  statusDialog.action === "activate"
                    ? "success.50"
                    : "error.50",
              }}
            >
              <Stack spacing={0.75}>
                <Typography variant="body2">
                  <strong>Member:</strong>{" "}
                  {get(statusDialog.row, "fullName", "FullName", "—")}
                </Typography>

                <Typography variant="body2">
                  <strong>MPC Limit:</strong>{" "}
                  {formatCurrency(
                    get(
                      statusDialog.row,
                      "approvalLimit",
                      "ApprovalLimit",
                      null,
                    ),
                  )}
                </Typography>

                <Typography variant="body2">
                  <strong>Sequence:</strong>{" "}
                  {get(statusDialog.row, "sequenceNo", "SequenceNo", "—")}
                </Typography>
              </Stack>
            </Paper>

            <Alert
              severity={
                statusDialog.action === "activate" ? "success" : "warning"
              }
            >
              {statusDialog.action === "activate"
                ? "This employee will be included in the active MPC approval group."
                : "This employee will be removed from the active MPC approval group but the configuration will be preserved."}
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeStatusDialog} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color={statusDialog.action === "activate" ? "success" : "error"}
            onClick={confirmStatusChange}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={18} />
            ) : statusDialog.action === "activate" ? (
              "Activate"
            ) : (
              "Make Inactive"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const SingleAuthorityEditor = ({
  title,
  description,
  routingKey,
  authorities,
  employees,
  saving,
  onSave,
}) => {
  const current = authorityRowsByKey(authorities, routingKey)[0] || null;
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [approvalLimit, setApprovalLimit] = useState("");

  useEffect(() => {
    if (!current) {
      setSelectedEmployee(null);
      setApprovalLimit("");
      return;
    }
    setSelectedEmployee(
      employees.find(
        (employee) =>
          Number(employee.userId) ===
          Number(get(current, "userId", "UserId", null)),
      ) || null,
    );
    setApprovalLimit(get(current, "approvalLimit", "ApprovalLimit", "") ?? "");
  }, [current, employees]);

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>

        {current ? (
          <UserSummary row={current} />
        ) : (
          <Alert severity="warning">Not configured.</Alert>
        )}

        <Autocomplete
          options={employees}
          value={selectedEmployee}
          onChange={(_, value) => {
            setSelectedEmployee(value);
            if (
              value?.empLimitNew !== null &&
              value?.empLimitNew !== undefined
            ) {
              setApprovalLimit(value.empLimitNew);
            }
          }}
          filterOptions={filterEmployees}
          getOptionLabel={(option) => option?.displayName || ""}
          isOptionEqualToValue={(option, value) =>
            Number(option.userId) === Number(value.userId)
          }
          renderInput={(params) => (
            <TextField {...params} label="Configured Employee" />
          )}
        />

        <TextField
          fullWidth
          type="number"
          label="Approval Limit"
          value={approvalLimit}
          onChange={(e) => setApprovalLimit(e.target.value)}
        />

        <Button
          variant="contained"
          disabled={
            saving ||
            !selectedEmployee ||
            (routingKey !== "PRESIDENT" && approvalLimit === "")
          }
          onClick={() =>
            onSave({
              action: "UPSERT",
              id: get(current, "id", "Id", null),
              routingKey,
              selectedUserId: selectedEmployee.userId,
              approvalLimit:
                approvalLimit === "" ? null : Number(approvalLimit),
              sequenceNo: 1,
              isActive: true,
            })
          }
        >
          Save {title}
        </Button>
      </Stack>
    </Paper>
  );
};

const EpoMaster = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [deleteRuleTarget, setDeleteRuleTarget] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);

  const load = useCallback(async (isRefresh = false) => {
    try {
      setError("");
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const [configResponse, officeResponse, limitResponse] = await Promise.all(
        [getEpoWorkflowConfiguration(), GetOfficeEmployee(), GetLimit()],
      );

      setConfig(configResponse?.data || null);

      const officeData = officeResponse?.data || {};
      const limitData = limitResponse?.data || {};
      const officeRows =
        officeData?.result ||
        officeData?.Result ||
        (Array.isArray(officeData) ? officeData : []);
      const limitRows =
        limitData?.result ||
        limitData?.Result ||
        (Array.isArray(limitData) ? limitData : []);
      setEmployees(mergeOfficeEmployeesWithLimits(officeRows, limitRows));
    } catch (loadError) {
      setError(
        getErrorMessage(
          loadError,
          "Unable to load EPO workflow configuration.",
        ),
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const reloadAfterSave = async (message) => {
    await load(true);
    setSuccessMessage(message);
  };

  const runSave = async (operation, successText) => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");
      await operation();
      await reloadAfterSave(successText);
      return true;
    } catch (saveError) {
      setError(getErrorMessage(saveError));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const rules = useMemo(
    () =>
      list(get(config, "rules", "Rules", [])).sort(
        (a, b) =>
          Number(get(a, "priority", "Priority", 0)) -
          Number(get(b, "priority", "Priority", 0)),
      ),
    [config],
  );

  const activeRules = useMemo(
    () => rules.filter((rule) => isActiveRow(rule)),
    [rules],
  );

  const inactiveRules = useMemo(
    () => rules.filter((rule) => !isActiveRow(rule)),
    [rules],
  );

  const steps = useMemo(
    () => list(get(config, "steps", "Steps", [])),
    [config],
  );

  const authorities = useMemo(
    () => list(get(config, "authorities", "Authorities", [])),
    [config],
  );

  const mpcMembers = authorityRowsByKey(authorities, "MPC_DEFAULT");
  const higherAuthorities = authorityRowsByKey(authorities, "HIGHER_AUTHORITY");
  const presidents = authorityRowsByKey(authorities, "PRESIDENT");
  const itApprovers = authorityRowsByKey(authorities, "IT_APPROVER_PRIMARY");
  const deputies = authorityRowsByKey(authorities, "DEPUTY_STAGE");
  const mpcRule = findRule(rules, "MPC");
  const [inactiveRulesExpanded, setInactiveRulesExpanded] = useState(false);

  const saveRule = async (payload) => {
    const ok = await runSave(
      () => saveEpoWorkflowRule(payload),
      "Workflow rule saved to the database.",
    );
    if (ok) {
      setRuleDialogOpen(false);
      setEditingRule(null);
    }
  };

  const deleteRule = (rule) => {
    setDeleteRuleTarget(rule);
  };

  const saveSteps = (workflowRuleId, draft) =>
    runSave(
      () =>
        saveEpoWorkflowSteps({
          workflowRuleId,
          steps: draft.map((row) => ({
            stageType: row.stageType,
            approverUserId:
              row.stageType === "SPECIFIC_USER" ? row.approverUserId : null,
          })),
        }),
      "Approval sequence saved to the database.",
    );

  const saveAuthority = (payload) =>
    runSave(
      () => saveEpoAuthority(payload),
      "Authority configuration saved to the database.",
    );

  const deleteAuthority = async (row) => {
    if (
      !window.confirm(
        `Remove ${get(row, "fullName", "FullName", "this authority")}?`,
      )
    )
      return;
    await runSave(
      () =>
        saveEpoAuthority({ action: "DELETE", id: get(row, "id", "Id", null) }),
      "Authority removed.",
    );
  };

  const saveThresholds = async ({ mpcLimit, higherLimit, itLimit }) => {
    const mpcDirectorRule = findRule(rules, "MPC_DIRECTOR");
    const higher = higherAuthorities[0] || null;
    const it = itApprovers[0] || null;

    await runSave(async () => {
      if (!mpcRule || !mpcDirectorRule || !higher || !it) {
        throw new Error(
          "Required MPC, Higher Authority or IT configuration is missing.",
        );
      }

      await saveEpoWorkflowRule(rulePayload(mpcRule, { maxAmount: mpcLimit }));
      await saveEpoWorkflowRule(
        rulePayload(mpcDirectorRule, { minAmount: mpcLimit }),
      );
      await saveEpoAuthority({
        action: "UPSERT",
        id: get(higher, "id", "Id", null),
        routingKey: "HIGHER_AUTHORITY",
        selectedUserId: get(higher, "userId", "UserId", null),
        approvalLimit: higherLimit,
        sequenceNo: 1,
        isActive: true,
      });
      await saveEpoAuthority({
        action: "UPSERT",
        id: get(it, "id", "Id", null),
        routingKey: "IT_APPROVER_PRIMARY",
        selectedUserId: get(it, "userId", "UserId", null),
        approvalLimit: itLimit,
        sequenceNo: 1,
        isActive: true,
      });
    }, "Approval thresholds saved to the database.");
  };

  const breadcrumbs = [
    { to: "/dashboard", title: "Dashboard" },
    { title: "EPO Workflow Configuration" },
  ];

  if (loading) {
    return (
      <PageContainer
        title="EPO Workflow Configuration"
        description="Manage dynamic EPO approval flow"
      >
        <Box sx={{ minHeight: 380, display: "grid", placeItems: "center" }}>
          <Stack spacing={1.5} alignItems="center">
            <CircularProgress />
            <Typography color="text.secondary">
              Loading dynamic EPO workflow…
            </Typography>
          </Stack>
        </Box>
      </PageContainer>
    );
  }

  const confirmBulkRuleAction = (isActive) => {
    setBulkAction(isActive);
  };

  return (
    <PageContainer
      title="EPO Workflow Configuration"
      description="Manage dynamic EPO approval flow"
    >
      <Breadcrumb title="EPO Workflow Configuration" items={breadcrumbs} />

      <Stack spacing={2.5}>
        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {successMessage ? (
          <Alert severity="success" onClose={() => setSuccessMessage("")}>
            {successMessage}
          </Alert>
        ) : null}

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              icon={<AccountTreeIcon />}
              label="Workflow Rules"
              value={rules.length}
              caption={`${
                steps.filter(isActiveRow).length
              } active approval steps`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              icon={<GroupsIcon />}
              label="MPC Members"
              value={mpcMembers.length}
              caption={`MPC upper limit ${formatCurrency(
                get(mpcRule, "maxAmount", "MaxAmount", null),
              )}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              icon={<AdminPanelSettingsIcon />}
              label="Higher Authority"
              value={
                higherAuthorities[0]
                  ? get(
                      higherAuthorities[0],
                      "fullName",
                      "FullName",
                      "Configured",
                    )
                  : "Missing"
              }
              caption={formatCurrency(
                get(
                  higherAuthorities[0],
                  "approvalLimit",
                  "ApprovalLimit",
                  null,
                ),
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              icon={<BusinessIcon />}
              label="Department Mappings"
              value={deputies.length}
              caption={`${presidents.length} President • ${itApprovers.length} IT Approver`}
            />
          </Grid>
        </Grid>

        <Paper variant="outlined">
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Approval Flows" />
            <Tab label="Department Routing" />
            <Tab label={`MPC & Authorities`} />
            <Tab iconPosition="start" label="Email Templates" />
            <Tab iconPosition="start" label="Mail Tokens" />
            <Tab label="Simulator" />
          </Tabs>
          <Divider />

          <Box sx={{ p: { xs: 1.25, md: 1.75 } }}>
            {tab === 0 ? (
              <Stack spacing={1.25}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Alert severity="info" sx={{ flex: 1 }}>
                    Approval rules and sequences below are live DB
                    configuration. Save Sequence writes directly to
                    master.EpoWorkflowStep.
                  </Alert>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={saving}
                    onClick={() => {
                      setEditingRule(null);
                      setRuleDialogOpen(true);
                    }}
                  >
                    Add New Rule
                  </Button>

                  <Button
                    variant="outlined"
                    color="success"
                    disabled={saving}
                    onClick={() => confirmBulkRuleAction(true)}
                  >
                    Activate All Rules
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    disabled={saving}
                    onClick={() => confirmBulkRuleAction(false)}
                  >
                    Deactivate All Rules
                  </Button>
                </Stack>

                <ThresholdEditor
                  rules={rules}
                  authorities={authorities}
                  saving={saving}
                  onSave={saveThresholds}
                />

                {activeRules.length > 0 ? (
                  <Stack spacing={1.25}>
                    <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                      Active Workflow Rules
                    </Typography>

                    {activeRules.map((rule) => (
                      <FlowCard
                        key={get(
                          rule,
                          "id",
                          "Id",
                          get(rule, "ruleCode", "RuleCode", ""),
                        )}
                        rule={rule}
                        allSteps={steps}
                        employees={employees}
                        saving={saving}
                        onSaveSteps={saveSteps}
                        onEditRule={(selectedRule) => {
                          setEditingRule(selectedRule);
                          setRuleDialogOpen(true);
                        }}
                        onDeleteRule={deleteRule}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">
                    No active workflow rules configured.
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                {inactiveRules.length > 0 ? (
                  <Accordion
                    expanded={inactiveRulesExpanded}
                    onChange={(_, expanded) =>
                      setInactiveRulesExpanded(expanded)
                    }
                    variant="outlined"
                    disableGutters
                    sx={{
                      mt: 2,
                      boxShadow: "none",
                      backgroundColor: "#F8F8F8",
                      "&:before": {
                        display: "none",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: 48,
                        "&.Mui-expanded": {
                          minHeight: 48,
                        },
                        "& .MuiAccordionSummary-content": {
                          my: 1,
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                          my: 1,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={700} color={"primary.main"}>
                          Inactive Workflow Rules
                        </Typography>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 0.5 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        These workflow rules are inactive and are not used in
                        the EPO approval flow.
                      </Typography>

                      <Stack spacing={1}>
                        {inactiveRules.map((rule) => (
                          <FlowCard
                            key={get(rule, "id", "Id", null)}
                            rule={rule}
                            allSteps={steps}
                            employees={employees}
                            saving={saving}
                            onSaveSteps={saveSteps}
                            onEditRule={(selectedRule) => {
                              setEditingRule(selectedRule);
                              setRuleDialogOpen(true);
                            }}
                            onDeleteRule={deleteRule}
                            inactive
                          />
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ) : null}
              </Stack>
            ) : null}

            {tab === 1 ? (
              <DepartmentRouting
                authorities={authorities}
                employees={employees}
                saving={saving}
                onSave={saveAuthority}
              />
            ) : null}

            {tab === 2 ? (
              <Stack spacing={2}>
                <MpcEditor
                  authorities={authorities}
                  employees={employees}
                  saving={saving}
                  onSave={saveAuthority}
                />

                <Grid
                  container
                  spacing={1.5}
                  sx={{
                    ml: -1,
                    mr: 1,
                    width: "calc(100% - 8px)",
                  }}
                >
                  <Grid item xs={12} lg={4}>
                    <SingleAuthorityEditor
                      title="Higher Authority"
                      description="Used after MPC and before President where configured."
                      routingKey="HIGHER_AUTHORITY"
                      authorities={authorities}
                      employees={employees}
                      saving={saving}
                      onSave={saveAuthority}
                    />
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <SingleAuthorityEditor
                      title="President"
                      description="Final authority for the highest normal flow and IT EPO above IT approver limit."
                      routingKey="PRESIDENT"
                      authorities={authorities}
                      employees={employees}
                      saving={saving}
                      onSave={saveAuthority}
                    />
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <SingleAuthorityEditor
                      title="IT Approver"
                      description="IT EPO above creator limit and within this person's approval limit."
                      routingKey="IT_APPROVER_PRIMARY"
                      authorities={authorities}
                      employees={employees}
                      saving={saving}
                      onSave={saveAuthority}
                    />
                  </Grid>
                </Grid>
              </Stack>
            ) : null}

            {tab === 3 ? (
              <EpoMailTemplateManager rules={rules} employees={employees} />
            ) : null}

            {tab === 4 ? <EpoMailTokenManager /> : null}

            {tab === 5 ? (
              <EpoWorkflowSimulator
                employeeRows={employees}
                employeeLoadError=""
              />
            ) : null}
          </Box>
        </Paper>
      </Stack>

      <RuleDialog
        open={ruleDialogOpen}
        rule={editingRule}
        nextPriority={
          (rules.reduce(
            (max, rule) =>
              Math.max(max, Number(get(rule, "priority", "Priority", 0))),
            0,
          ) || 0) + 10
        }
        saving={saving}
        onClose={() => {
          setRuleDialogOpen(false);
          setEditingRule(null);
        }}
        onSave={saveRule}
      />

      <Dialog
        open={bulkAction !== null}
        onClose={() => {
          if (!saving) {
            setBulkAction(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {bulkAction
            ? "Activate All Workflow Rules?"
            : "Deactivate All Workflow Rules?"}
        </DialogTitle>

        <DialogContent dividers>
          <Typography>
            {bulkAction
              ? "Are you sure you want to activate all workflow rules?"
              : "Are you sure you want to deactivate all workflow rules?"}
          </Typography>

          {!bulkAction ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Deactivating all rules will disable the workflow rules for EPO
              processing.
            </Alert>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setBulkAction(null)} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color={bulkAction ? "success" : "error"}
            disabled={saving}
            onClick={async () => {
              try {
                setSaving(true);
                setError("");
                setSuccessMessage("");

                await BulkActivateEpoWorkflowRules({
                  isActive: bulkAction,
                });

                await load(true);

                setSuccessMessage(
                  bulkAction
                    ? "All workflow rules activated."
                    : "All workflow rules deactivated.",
                );

                setBulkAction(null);
              } catch (error) {
                setError(getErrorMessage(error));
              } finally {
                setSaving(false);
              }
            }}
          >
            {bulkAction ? "Activate All" : "Deactivate All"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deleteRuleTarget)}
        onClose={saving ? undefined : () => setDeleteRuleTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Workflow Rule?</DialogTitle>

        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to delete this workflow rule?
          </Typography>

          {deleteRuleTarget ? (
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1.5,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {get(deleteRuleTarget, "ruleName", "RuleName", "Workflow Rule")}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                Rule Code: {get(deleteRuleTarget, "ruleCode", "RuleCode", "—")}
              </Typography>
            </Paper>
          ) : null}

          <Alert severity="warning" sx={{ mt: 2 }}>
            This will deactivate the workflow rule and its approval steps.
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteRuleTarget(null)} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={async () => {
              if (!deleteRuleTarget) return;

              const ruleId = get(deleteRuleTarget, "id", "Id", null);

              const ok = await runSave(
                () =>
                  saveEpoWorkflowRule({
                    action: "DELETE",
                    id: ruleId,
                  }),
                "Workflow rule deleted.",
              );

              if (ok) {
                setDeleteRuleTarget(null);
              }
            }}
          >
            {saving ? "Deleting..." : "Delete Rule"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default EpoMaster;
